import React, { useState } from "react";

export default 
function LinkedInPreviewCard({ caption, articleUrl, articleTitle, articleImage, articleDomain, userName, userHeadline, userAvatar, scheduledLabel, noLabel, captionLimit, analyticsUrl, isPublished }) {
  const LI = "#0a66c2"; // LinkedIn blue
  const [expanded, setExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [hoveredMenu, setHoveredMenu] = useState(false);

  function renderCaption(text) {
    if (!text) return null;
    const parts = text.split(/([@#]\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith("#")) {
        const tag = part.slice(1);
        return (
          <a key={i} href={`https://www.linkedin.com/feed/hashtag/?keywords=${tag}`}
            target="_blank" rel="noopener noreferrer"
            style={{ color: LI, fontWeight: 600, textDecoration: "none" }}
            onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}>
            {part}
          </a>
        );
      }
      if (part.startsWith("@")) {
        return <span key={i} style={{ color: LI, fontWeight: 600 }}>{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  }

  const headline = (userHeadline || "").substring(0, 80);
  const displayName = userName || "You";
  const initials = displayName.split(" ").map(w => w[0]).join("").toUpperCase().substring(0, 2);

  const CAPTION_LIMIT = captionLimit || 240;
  const captionText = caption || "";
  const isTruncated = !expanded && captionText.length > CAPTION_LIMIT;
  const visibleCaption = isTruncated ? captionText.substring(0, CAPTION_LIMIT) : captionText;

  const timestamp = scheduledLabel || "Just now · 🌐";

  let domain = articleDomain || "";
  if (!domain && articleUrl) {
    try { domain = new URL(articleUrl).hostname.replace(/^www\./, ""); } catch {}
  }

  const hasImage = !!(articleImage && !imgError);

  const actions = [
    { label: "Like", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg> },
    { label: "Comment", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
    { label: "Repost", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg> },
    { label: "Send", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> },
  ];

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      {!noLabel && (
        <div style={{ fontSize: 12, color: "#888", textAlign: "center", marginBottom: 10 }}>
          Preview — how this will look on LinkedIn
        </div>
      )}

      {/* Card */}
      <div style={{ background: "#fff", border: "1px solid #e0dfdc", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 1px rgba(0,0,0,0.04)" }}>

        {/* ── Header ── */}
        <div style={{ padding: "12px 16px 0", display: "flex", alignItems: "flex-start", gap: 10 }}>
          {/* Avatar 48px, top-aligned */}
          {userAvatar ? (
            <img src={userAvatar} alt="" style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
          ) : (
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#a08f7d", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 600, fontSize: 15, flexShrink: 0 }}>
              {initials || "Y"}
            </div>
          )}

          {/* Meta block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Line 1: Name · You */}
            <div style={{ fontSize: 16, fontWeight: 600, color: "#191919", lineHeight: 1.25 }}>
              {displayName}
              <span style={{ fontSize: 14, fontWeight: 400, color: "#666666", marginLeft: 5 }}>· You</span>
            </div>
            {/* Line 2: role/company */}
            {headline && (
              <div style={{ fontSize: 14, color: "#666666", lineHeight: 1.3, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {headline}
              </div>
            )}
            {/* Line 3: timestamp + globe */}
            <div style={{ fontSize: 13, color: "#666666", marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
              {timestamp}
            </div>
          </div>

          {/* Overflow menu only — no +Follow on self posts */}
          <div
            onMouseEnter={() => setHoveredMenu(true)}
            onMouseLeave={() => setHoveredMenu(false)}
            style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: hoveredMenu ? "#f3f2ef" : "transparent", cursor: "pointer", flexShrink: 0, fontSize: 20, color: "#666666", letterSpacing: 1, userSelect: "none", transition: "background 0.1s" }}
          >
            ···
          </div>
        </div>

        {/* ── Body text ── */}
        <div style={{ padding: "8px 16px", fontSize: 15, lineHeight: 1.5, color: "#191919", whiteSpace: "pre-wrap", wordBreak: "break-word", marginBottom: articleUrl ? 0 : 2 }}>
          {renderCaption(visibleCaption)}
          {isTruncated && (
            <span onClick={() => setExpanded(true)} style={{ cursor: "pointer" }}>
              …<span style={{ color: "#666666" }}>see more</span>
            </span>
          )}
          {expanded && captionText.length > CAPTION_LIMIT && (
            <span onClick={() => setExpanded(false)} style={{ color: "#666666", cursor: "pointer", fontSize: 14 }}> show less</span>
          )}
        </div>

        {/* ── Horizontal link preview card ── */}
        {articleUrl && (
          <a href={articleUrl} target="_blank" rel="noopener noreferrer" className="li-preview-link"
            style={{ margin: "10px 16px 8px", border: "1px solid #e0dfdc", borderRadius: 8, overflow: "hidden", display: "flex", minHeight: 88, background: "#fff", textDecoration: "none", color: "inherit" }}>
            {/* Thumbnail — 108×88, left side */}
            <div style={{ width: 108, minHeight: 88, flexShrink: 0, background: "#dce6ee", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              {hasImage ? (
                <img src={articleImage} alt="" style={{ width: 108, height: "100%", objectFit: "cover", display: "block" }} onError={() => setImgError(true)} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a0b0c0" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
              )}
            </div>
            {/* Preview text — right side */}
            <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, minWidth: 0 }}>
              {articleTitle && (
                <div className="li-preview-title" style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: 4 }}>
                  {articleTitle}
                </div>
              )}
              {domain && (
                <div style={{ fontSize: 13, color: "#666666" }}>{domain}</div>
              )}
            </div>
          </a>
        )}

        {/* ── Action row ── */}
        <div style={{ borderTop: "1px solid #ebebeb", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", marginTop: 2 }}>
          {actions.map(({ label, icon }) => (
            <div
              key={label}
              onMouseEnter={() => setHoveredAction(label)}
              onMouseLeave={() => setHoveredAction(null)}
              style={{ height: 44, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 14, fontWeight: 500, color: hoveredAction === label ? "#191919" : "#666666", background: hoveredAction === label ? "#f3f2ef" : "transparent", cursor: "pointer", userSelect: "none", transition: "background 0.1s, color 0.1s" }}
            >
              {icon}<span>{label}</span>
            </div>
          ))}
        </div>

        {/* ── Analytics row (published self-posts only) ── */}
        {isPublished && (
          <div style={{ borderTop: "1px solid #ebebeb", padding: "6px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 500, color: "#666666" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              Impressions
            </div>
            {analyticsUrl && (
              <a href={analyticsUrl} target="_blank" rel="noreferrer" style={{ fontSize: 14, fontWeight: 600, color: LI, textDecoration: "none" }}>
                View analytics
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
